import {DateUtils} from '@wallaby-ui/core';
import {MerieSchema, ShortcutEnum, TableSorting} from '@wallaby-ui/document';

export type InterventionsDepartmentReportCategoryStatisticsRowRiskiness =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsRowRiskiness'];
export type InterventionsDepartmentReportCategoryStatisticsChartRowRiskiness =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsChartRowRiskiness'];
export type InterventionsDepartmentReportCategoryStatisticsRowLong =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsRowLong'];
export type InterventionsDepartmentReportCategoryStatisticsChartRowLong =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsChartRowLong'];
export type InterventionsDepartmentReportCategoryStatisticsRowDrpType =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsRowDrpType'];
export type InterventionsDepartmentReportCategoryStatisticsChartRowDrpType =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsRowDrpType'];
export type InterventionsDepartmentReportCategoryStatisticsRowDrpCause =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsRowDrpCause'];
export type InterventionsDepartmentReportCategoryStatisticsChartRowDrpCause =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsChartRowDrpCause'];
export type InterventionsDepartmentReportCategoryStatisticsRowDrpPlannedIntervention =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsRowDrpPlannedIntervention'];
export type InterventionsDepartmentReportCategoryStatisticsChartRowDrpPlannedIntervention =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsChartRowDrpPlannedIntervention'];
export type InterventionsDepartmentReportCategoryStatisticsRiskiness =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsRiskiness'];
export type InterventionsDepartmentReportCategoryStatisticsLong =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsLong'];
export type InterventionsDepartmentReportCategoryStatisticsDrpType =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsDrpType'];
export type InterventionsDepartmentReportCategoryStatisticsDrpCause =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsDrpCause'];
export type InterventionsDepartmentReportCategoryStatisticsDrpPlannedIntervention =
  MerieSchema.components['schemas']['InterventionsDepartmentReportCategoryStatisticsDrpPlannedIntervention'];
export type InterventionsDepartmentReportBasicMeasuresResultDto =
  MerieSchema.components['schemas']['InterventionsDepartmentReportBasicMeasuresResultDto'];
export type InterventionsDepartmentReportDto = MerieSchema.components['schemas']['InterventionsDepartmentReportDto'];
export type InterventionsDepartmentReportFilter = MerieSchema.components['schemas']['InterventionsDepartmentReportFilter'] & {
  floatingShortcutName?: ShortcutEnum | null;
};
export type BasicMeasuresRowType = InterventionsDepartmentReportBasicMeasuresResultDto;
export type ChartTypeType = InterventionsDepartmentReportFilter['chartTypeSelection'];
export type ChartValueType = InterventionsDepartmentReportFilter['chartValueSelection'];

export const CHART_TYPE_OPTIONS: {id: ChartTypeType; messageKey: string}[] = [
  {id: 'LINE_CHART', messageKey: 'department.report.charts.type.line'},
  {id: 'STACKED_LINE_CHART', messageKey: 'department.report.charts.type.stacked'},
];

export enum ReportType {
  RISK_CATEGORY = 'RISK_CATEGORY',
  DRPS_PER_ATC = 'DRPS_PER_ATC',
  TYPES_OF_PROBLEMS = 'TYPES_OF_PROBLEMS',
  CAUSES_OF_PROBLEMS = 'CAUSES_OF_PROBLEMS',
  PLANNED_INTERVENTIONS = 'PLANNED_INTERVENTIONS',
}

export type FiltersData = {
  report: InterventionsDepartmentReportFilter;
  basicMeasuresTableSorting: TableSorting;
  riskCategoryTableSorting: TableSorting;
  drpsPerAtcTableSorting: TableSorting;
  typesOfProblemTableSorting: TableSorting;
  causesOfProblemTableSorting: TableSorting;
  plannedInterventionsTableSorting: TableSorting;
  savedFiltersOpen: boolean;
};

type ElemOfArray<T> = T extends ReadonlyArray<infer U> ? U : T extends (infer U)[] ? U : never;

type ChartsElemMap<T> = {
  [K in keyof T as T[K] extends {charts: readonly any[]} ? K : never]: T[K] extends {charts: infer A} ? ElemOfArray<A> : never;
};

export type ChartsElemUnion<T> = ChartsElemMap<T>[keyof ChartsElemMap<T>];

export const CHART_COLORS = ['#FF7EB6', '#BE95FF', '#FFD74A', '#3DDBD9', '#6FDC8C', '#878D96'];

export const DEFAULT_BASIC_MEASURES_TABLE_SORTING: TableSorting = {
  field: 'measureCount',
  direction: 'desc',
};

export const DEFAULT_DEPARTMENT_TABLE_SORTING: (reportType: ReportType) => TableSorting = (reportType: ReportType) => ({
  field: `measureCount`,
  direction: 'desc',
});

export type DepartmentTableRowType = {
  category: string;
  measureCount: number;
  measureCountToHospCountPct: number;
  measureCountPer1000HospDays: number;
};
export type DepartmentTableRowTypeWithTotal = DepartmentTableRowType & {isTotalRow?: boolean};
export type DepartmentTableRowsTypeWithTotal = DepartmentTableRowTypeWithTotal[];

export type SortingProperty = Partial<
  Pick<
    FiltersData,
    | 'riskCategoryTableSorting'
    | 'drpsPerAtcTableSorting'
    | 'typesOfProblemTableSorting'
    | 'causesOfProblemTableSorting'
    | 'plannedInterventionsTableSorting'
  >
>;
export type TableOps = {
  defaultSorting: TableSorting;
  getSortingProp: (filters: FiltersData) => TableSorting;
  getSortingUpdate: (newSorting: TableSorting) => SortingProperty;
};
export const reportTypeToTableOps: Record<ReportType, TableOps> = {
  CAUSES_OF_PROBLEMS: {
    defaultSorting: DEFAULT_DEPARTMENT_TABLE_SORTING(ReportType.CAUSES_OF_PROBLEMS),
    getSortingProp: (filters) => filters.causesOfProblemTableSorting,
    getSortingUpdate: (newSorting) => ({causesOfProblemTableSorting: newSorting}),
  },
  DRPS_PER_ATC: {
    defaultSorting: DEFAULT_DEPARTMENT_TABLE_SORTING(ReportType.DRPS_PER_ATC),
    getSortingProp: (filters) => filters.drpsPerAtcTableSorting,
    getSortingUpdate: (newSorting) => ({drpsPerAtcTableSorting: newSorting}),
  },
  PLANNED_INTERVENTIONS: {
    defaultSorting: DEFAULT_DEPARTMENT_TABLE_SORTING(ReportType.PLANNED_INTERVENTIONS),
    getSortingProp: (filters) => filters.plannedInterventionsTableSorting,
    getSortingUpdate: (newSorting) => ({plannedInterventionsTableSorting: newSorting}),
  },
  RISK_CATEGORY: {
    defaultSorting: DEFAULT_DEPARTMENT_TABLE_SORTING(ReportType.RISK_CATEGORY),
    getSortingProp: (filters) => filters.riskCategoryTableSorting,
    getSortingUpdate: (newSorting) => ({riskCategoryTableSorting: newSorting}),
  },
  TYPES_OF_PROBLEMS: {
    defaultSorting: DEFAULT_DEPARTMENT_TABLE_SORTING(ReportType.TYPES_OF_PROBLEMS),
    getSortingProp: (filters) => filters.typesOfProblemTableSorting,
    getSortingUpdate: (newSorting) => ({typesOfProblemTableSorting: newSorting}),
  },
};

export type ChartTableLabels = {
  categoryColumnTitle: string;
  chartTitle: string;
  tableTitle: string;
};
export const reportTypeToChartTableLabels: Record<ReportType, ChartTableLabels> = {
  CAUSES_OF_PROBLEMS: {
    categoryColumnTitle: 'department.report.charttable.column.causeOfProblem',
    chartTitle: 'department.report.charttable.chart.causesOfProblems.title',
    tableTitle: 'department.report.charttable.table.causesOfProblems.title',
  },
  DRPS_PER_ATC: {
    categoryColumnTitle: 'department.report.charttable.column.atcCode',
    chartTitle: 'department.report.charttable.chart.drpsPerAtc.title',
    tableTitle: 'department.report.charttable.table.drpsPerAtc.title',
  },
  PLANNED_INTERVENTIONS: {
    categoryColumnTitle: 'department.report.charttable.column.plannedIntervention',
    chartTitle: 'department.report.charttable.chart.plannedInterventions.title',
    tableTitle: 'department.report.charttable.table.plannedInterventions.title',
  },
  RISK_CATEGORY: {
    categoryColumnTitle: 'department.report.charttable.column.riskCategory',
    chartTitle: 'department.report.charttable.chart.riskCategory.title',
    tableTitle: 'department.report.charttable.table.riskCategory.title',
  },
  TYPES_OF_PROBLEMS: {
    categoryColumnTitle: 'department.report.charttable.column.typeOfProblem',
    chartTitle: 'department.report.charttable.chart.typesOfProblem.title',
    tableTitle: 'department.report.charttable.table.typesOfProblem.title',
  },
};
export const DEFAULT_DEPARTMENT_REPORT_CHART_TYPE: ChartTypeType = 'STACKED_LINE_CHART';
export const DEFAULT_DEPARTMENT_REPORT_FILTERS = (): Partial<InterventionsDepartmentReportFilter> => {
  const [startOfLastMonth, endOfLastMonth] = DateUtils.lastCompletedMonth();
  return {
    dateFrom: startOfLastMonth,
    dateTo: endOfLastMonth,
    floatingShortcutName: ShortcutEnum.PREVIOUS_MONTH,
    measureType: 'ALL',
    causesAggregationType: 'ALL',
    chartValueSelection: 'ABSOLUTE_COUNTS',
    chartTypeSelection: DEFAULT_DEPARTMENT_REPORT_CHART_TYPE,
  };
};

/**
 * The report filter without the `organizationId` the defaults carried before PDW-393: a single hospital id taken over
 * from the hospital report, read nowhere here and not a field of the filter DTO, so a saved filter holding it would be
 * refused. Only a page state kept in the browser can still hold it.
 */
export function withoutLegacyOrganizationId<T extends Record<string, any>>(report: T): T {
  if (report == null || !('organizationId' in report)) return report;
  const {organizationId: _organizationId, ...rest} = report;
  return rest as T;
}

export const getChartSplitLayoutId = (reportType: ReportType) => `/LightSplitLayout.MerieDepartmentReportLayout.${reportType}.Charts`;
export const getMainSplitLayoutId = () => '/LightSplitLayout.MerieDepartmentReportLayout';
